import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import CircularProgress from "./CircularProgress";
import { GetImages } from "../../utils/GetImages";
import "./HorizontalFileUploader.css";

interface HorizontalMultipleFileUploaderProps {
  name: string;
  uploaderId: string;
  label: string;
  uploadertext?: string;
  required?: boolean;
  disabled?: boolean;
  requiredMessage?: string;
  allowedFormats?: string[];
  maxSizeMB?: number;
  maxFiles?: number;
  resetTrigger?: number;
  uploadertypeslabel?: string;
  defaultImages?: Array<{ src: string; name: string }>;
}

const HorizontalMultipleFileUploader = ({
  name,
  uploaderId,
  label,
  required = false,
  disabled = false,
  requiredMessage = "This field is required",
  allowedFormats = ["image/jpeg", "image/png", "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  maxSizeMB = 5,
  maxFiles = 5,
  resetTrigger = 0,
  uploadertext = "Upload Document",
  uploadertypeslabel = `Max. size ${maxSizeMB}MB. ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")}`,
  defaultImages = [],
}: HorizontalMultipleFileUploaderProps) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  const [filesMap, setFilesMap] = useState<Record<string, { file: File; preview?: string }[]>>({});
  const files = filesMap[name] || [];
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Normalize base64 to data URL
  const normalizeBase64Url = (input: string) => {
    if (input.startsWith('data:')) return input;
    if (input.startsWith('iVBORw0KGgo=')) return `data:image/png;base64,${input}`;
    if (input.startsWith('/9j/')) return `data:image/jpeg;base64,${input}`;
    return `data:image/png;base64,${input}`;
  };

  // Convert any base64 to PNG data URL using canvas
  const convertToPNGDataURL = (base64Input: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } else {
          reject(new Error('Canvas context not available'));
        }
      };
      img.onerror = reject;
      img.src = normalizeBase64Url(base64Input);
    });
  };

  // Convert base64 to PNG File object
  const base64ToPNGFile = async (base64Input: string, fileName: string = 'image.png'): Promise<File | null> => {
    try {
      const pngDataUrl = await convertToPNGDataURL(base64Input);
      const base64Data = pngDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      return new File([blob], fileName, { type: 'image/png' });
    } catch (error) {
      console.error('Base64 to PNG conversion error:', error);
      return null;
    }
  };

  // Initialize with defaultImages
  useEffect(() => {
    if (!defaultImages || !defaultImages.length || initializedRef.current) return;
    initializedRef.current = true;

    const processDefaultImages = async () => {
      const processedFiles: { file: File; preview?: string }[] = [];
      for (let i = 0; i < defaultImages.length && i < maxFiles; i++) {
        const defaultImage = defaultImages[i];
        const normalized = normalizeBase64Url(defaultImage.src);

        const file = await base64ToPNGFile(defaultImage.src, defaultImage.name);
        if (file) {
          processedFiles.push({ file, preview: normalized });
        }
      }
      if (processedFiles.length > 0) {
        setFilesMap((prev) => ({ ...prev, [name]: processedFiles }));
        setValue(name, processedFiles.map((item) => item.file), { shouldValidate: true });
      }
    };

    processDefaultImages();
  }, [defaultImages, name, setValue, maxFiles]);

  // Reset on trigger
  useEffect(() => {
    if (resetTrigger === 0) return;
    setFilesMap((prev) => ({ ...prev, [name]: [] }));
    setValue(name, [], { shouldValidate: true });
    clearErrors(name);
    initializedRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetTrigger, setValue, clearErrors, name]);

  const clearProgress = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return prev;
        }
        return prev + 10;
      });
    }, 100);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("image")) return GetImages.imageIcon || GetImages.upload;
    if (fileType.includes("pdf")) return GetImages.pdfImage || GetImages.upload;
    if (fileType.includes("msword") || fileType.includes("wordprocessingml")) return GetImages.DocIcon || GetImages.upload;
    if (fileType.includes("ms-excel") || fileType.includes("spreadsheetml")) return GetImages.ExcelIcon || GetImages.upload;
    return GetImages.upload;
  };

  const validateFile = (file: File | string) => {
    if (file instanceof File) {
      if (!allowedFormats.includes(file.type)) {
        return `Only ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")} allowed`;
      }
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    if (typeof file === 'string') {
      const normalized = normalizeBase64Url(file);
      const mimeMatch = normalized.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/png";

      if (!allowedFormats.includes(mimeType)) {
        return `Only ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")} allowed`;
      }

      let base64Data = normalized.split(',')[1] || normalized;
      const sizeInBytes = Math.floor((base64Data.length * 3) / 4);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    return `Invalid file format`;
  };

  const handleFileChange = async (newFiles: FileList | null, fieldOnChange: (value: File[] | null) => void) => {
    if (!newFiles || newFiles.length === 0) {
      return;
    }

    clearErrors(name);

    if (files.length + newFiles.length > maxFiles) {
      setError(name, {
        type: "manual",
        message: `You can upload maximum ${maxFiles} files`,
      });
      return;
    }

    const validFiles: { file: File; preview?: string }[] = [];
    let validationError = "";

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      const result = validateFile(file);
      if (result !== true) {
        validationError = result;
        break;
      }

      if (file.type.includes("image")) {
        const reader = new FileReader();
        const pngFilePromise = new Promise<{ file: File; preview: string }>((resolve, reject) => {
          reader.onload = async (e) => {
            const base64 = e.target?.result as string;
            try {
              const pngDataUrl = await convertToPNGDataURL(base64);
              const pngFile = await base64ToPNGFile(base64, file.name.replace(/\.[^/.]+$/, ".png"));
              if (pngFile) {
                resolve({ file: pngFile, preview: pngDataUrl });
              } else {
                reject(new Error("Failed to convert image to PNG"));
              }
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        try {
          const result = await pngFilePromise;
          validFiles.push(result);
        } catch (error) {
          validationError = "Image conversion failed";
          break;
        }
      } else {
        validFiles.push({ file });
      }
    }

    if (validationError) {
      setError(name, {
        type: "manual",
        message: validationError,
      });
      return;
    }

    if (validFiles.length === 0) {
      return;
    }

    setLoading(true);
    clearProgress();

    setTimeout(() => {
      setLoading(false);
      const updatedFiles = [...files, ...validFiles];
      setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));
      const fileArray = updatedFiles.map((item) => item.file);
      setValue(name, fileArray, { shouldValidate: true });
      fieldOnChange(fileArray);
    }, 1500);
  };

 const handleDelete = (index: number, onChange: (value: File[] | null) => void) => {
  const updatedFiles = files.filter((_, i) => i !== index);
  setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));

  const fileArray = updatedFiles.map((item) => item.file);
  const valueToSet = fileArray.length > 0 ? fileArray : null;

  setValue(name, valueToSet, { shouldValidate: true });
  onChange(valueToSet);
  clearErrors(name);

  
   if (fileInputRef.current) fileInputRef.current && (fileInputRef.current.value = "");
};

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (files: File[] | null) => {
          if (required && (!files || files.length === 0)) return requiredMessage;
          if (files) {
            for (const file of files) {
              const result = validateFile(file);
              if (result !== true) return result;
            }
          }
          return true;
        },
      }}
      render={({ field: { onChange } }) => (
        <div className="form-group">
          <label className="fw-600">
            {label}{" "}
            {required ? (
              <span className="text-danger">*</span>
            ) : (
              <span className="optional-text-color">(Optional)</span>
            )}
          </label>
          <div className="horizontal-uploaderContainer">
            <div className={`horizontal-uploader-box-container border-image-dashed ${loading ? "uploding-bg-color" : ""}`}>
              <input
                disabled={disabled}
                type="file"
                ref={fileInputRef}
                id={`upload_${uploaderId}`}
                className="upload-input"
                onChange={(e) => {
                  handleFileChange(e.target.files, onChange);
                }}
                accept={allowedFormats.join(",")}
                multiple
              />
              <label htmlFor={`upload_${uploaderId}`} className="horizontal-upload-label">
                <div className="horizontal-upload-content">
                  <div className="horizontal-icon-section">
                    {loading ? (
                      <CircularProgress
                        size={40}
                        thickness={3}
                        isIcon={false}
                        color="blue"
                        BG_transparent={true}
                        progress={progress}
                      />
                    ) : (
                      <img className="horizontal-upload-icon iconStyleWithWhiteFilter" src={GetImages.upload} alt="upload" />
                    )}
                  </div>
                  <div className="horizontal-text-section">
                    <div className="horizontal-main-text">{uploadertext}</div>
                    <p className="horizontal-sub-text">{uploadertypeslabel}</p>
                  </div>
                </div>
              </label>
            </div>

            {/* Uploaded Files - Horizontal Layout */}
            {files.length > 0 && (
              <div className="horizontal-files-container">
                {files.map((item, index) => (
                  <div key={index} className="horizontal-file-item">
                    <div className="horizontal-file-content">
                      <div className="horizontal-file-icon-wrapper">
                        {item.file.type.includes("image") && item.preview ? (
                          <img
                            src={item.preview}
                            alt={`preview-${index}`}
                            className="horizontal-file-image-preview"
                          />
                        ) : (
                          <img
                            src={getFileIcon(item.file.type)}
                            alt={`icon-${index}`}
                            className="horizontal-file-type-icon"
                          />
                        )}
                      </div>
                      <span className="horizontal-file-name">{item.file.name}</span>
                      
                      <img className="delete-icon" src={GetImages.deleteIconRed} alt="success" width="22" height="22" onClick={() => handleDelete(index, onChange)} />
                      
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors[name] && (
              <div className="invalid-feedback d-block">{errors[name]?.message as string}</div>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default HorizontalMultipleFileUploader;